/**
 * Entrances: relays near far-away players (Hong Kong for China, New York for America...) that they can open instead of
 * the main address, for a better route to the same game. The server lists them (`entrances` in its server info, from
 * ENTRANCES) with the main address (`mainSite`). For a far-away player (ping over 100 ms), the menu times the way to
 * the game through each other way in, and offers the fastest only when it's clearly faster than the way in use: so a
 * player in China is offered the China entrance and a player in America the America one, and nobody nearby is
 * offered anything.
 */

export interface WayIn {
    /** what players see: "China", "America"... (the main address: "main") */
    readonly name: string
    /** where it is (an origin: scheme, host, port) */
    readonly url: string
}

export interface Offer extends WayIn {
    /** the ping that way (ms) */
    readonly ping: number
    /** the ping the way in use (ms) */
    readonly current: number
}

/** a player is far away (and worth timing the entrances for) above this ping (ms) */
export const FAR_PING_MS = 100;

/** time the entrances again at most this often (ms) */
const MEASURE_EVERY_MS = 30_000;

/** how many recent measurements are kept per way in: the lowest is its ping (a busy page only ever adds time) */
const KEEP = 3;

const originOf = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:" ? url.origin : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Which way in to offer, if any: the fastest one, only when it saves at least 40 ms and 15% of the ping of the way in
 * use (measurements wobble by a few dozen ms; a small gain isn't worth a new address and signing in again).
 */
export const pickWayIn = (current: number, measured: ReadonlyArray<WayIn & { readonly ping: number }>): Offer | undefined => {
    if (!Number.isFinite(current)) return undefined;
    let best: (WayIn & { readonly ping: number }) | undefined;
    for (const way of measured) {
        if (!Number.isFinite(way.ping)) continue;
        if (!best || way.ping < best.ping) best = way;
    }
    if (!best || best.ping > current - 40 || best.ping > current * 0.85) return undefined;
    return { name: best.name, url: best.url, ping: Math.round(best.ping), current: Math.round(current) };
};

let ways: WayIn[] = [];
let offer: Offer | undefined;
const recent = new Map<string, number[]>();
let measuredAt = 0;
let measuring = false;

const remember = (key: string, ms: number): number => {
    const list = recent.get(key) ?? [];
    list.push(ms);
    while (list.length > KEEP) list.shift();
    recent.set(key, list);
    return Math.min(...list);
};

/** every entrance's address the server listed (this one's too), and the main address: where Chinese opens (1.04.4) */
let listedOrigins: string[] = [];
let mainOrigin: string | undefined;
/** the entrances' addresses the server listed (this one's too) */
export const listedEntranceOrigins = (): readonly string[] => listedOrigins;
/** the main address the server gave (PUBLIC_SITE_URL's origin), if it gave one */
export const mainSiteOrigin = (): string | undefined => mainOrigin;
let markKnown: () => void = () => undefined;
/** settled once the server info said them (picking Chinese waits for it: the China entrance may be listed) */
export const entrancesKnown: Promise<void> = new Promise(resolve => {
    markKnown = resolve;
});

/** from the server info: its entrances, and the main address (both only matter when there are entrances) */
export const setEntrances = (entrances: unknown, mainSite: unknown): void => {
    const here = typeof location === "undefined" ? undefined : location.origin;
    const found: WayIn[] = [];
    listedOrigins = Array.isArray(entrances) ? (entrances as Array<{ url?: unknown }>).map(entry => originOf(entry?.url)).filter((url): url is string => url !== undefined) : [];
    mainOrigin = originOf(mainSite);
    if (Array.isArray(entrances)) {
        for (const entry of entrances as Array<{ name?: unknown, url?: unknown }>) {
            const url = originOf(entry?.url);
            const name = typeof entry?.name === "string" && /^[A-Za-z][A-Za-z0-9-]{0,23}$/u.test(entry.name) ? entry.name : undefined;
            if (url && name && url !== here && !found.some(way => way.url === url)) found.push({ name, url });
        }
    }
    const main = originOf(mainSite);
    // the main address is a way in too, for a player who came through an entrance that isn't the best for them
    if (found.length && main && main !== here && !found.some(way => way.url === main)) found.push({ name: "main", url: main });
    ways = found;
    markKnown();
};

/** the way in to offer this player now, if any */
export const wayInOffer = (): Offer | undefined => offer;

/** how long the game answers through `url` (ms): the lowest of three tries, or nothing if it can't be reached */
const timeTo = async (url: string): Promise<number | undefined> => {
    let best = Infinity;
    for (let i = 0; i < 3; i++) {
        const started = performance.now();
        try {
            // (an opaque answer is enough: only the time it takes matters)
            await fetch(`${url}/api/serverInfo`, { mode: "no-cors", cache: "no-store", credentials: "omit" });
            best = Math.min(best, performance.now() - started);
        } catch {
            return undefined;
        }
    }
    return best;
};

/**
 * A new measurement of the way in use (the menu's ping, ms): for a far-away player, now and then, the other ways in are
 * timed too, and the offer is brought up to date. Returns once the offer is current (nothing to time: at once).
 */
export const noteCurrentPing = async (ms: number): Promise<void> => {
    const current = remember("current", ms);
    if (!ways.length || current <= FAR_PING_MS) {
        offer = undefined;
        return;
    }
    if (!measuring && Date.now() - measuredAt >= MEASURE_EVERY_MS) {
        measuring = true;
        try {
            for (const way of ways) {
                const time = await timeTo(way.url);
                if (time !== undefined) remember(way.url, time);
            }
            measuredAt = Date.now();
        } finally {
            measuring = false;
        }
    }
    offer = pickWayIn(current, ways.map(way => ({ ...way, ping: Math.min(...(recent.get(way.url) ?? [Infinity])) })));
};
